import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, FolderOpen, Check, AlertCircle, Trash2, Copy, Pencil } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  useProjects,
  useCurrentProject,
  useLoadProject,
  useCreateProject,
  useDeleteProject,
  useDuplicateProject
} from '../../../hooks/useProjectData';
import { apiClient } from '../../../services/api/client';
import { toast } from 'react-hot-toast';

interface Project {
  id: string;
  name: string;
  description?: string;
  status: string;
  created_at: string;
  updated_at?: string;
  has_data?: {
    consumption: boolean;
    bill: boolean;
    prices: boolean;
    complete: boolean;
  };
}

export function ProjectSelector() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Use React Query hooks
  const { data: projects = [], isLoading: loadingProjects, error: projectsError } = useProjects();
  const { data: currentProject, isLoading: loadingCurrent, error: currentError } = useCurrentProject();
  const loadProjectMutation = useLoadProject();
  const createProjectMutation = useCreateProject();
  const deleteProjectMutation = useDeleteProject();
  const duplicateProjectMutation = useDuplicateProject();

  const loading = loadingProjects || loadingCurrent ||
                 loadProjectMutation.isLoading ||
                 createProjectMutation.isLoading ||
                 deleteProjectMutation.isLoading ||
                 duplicateProjectMutation.isLoading;

  const loadProject = async (projectId: string) => {
    try {
      await loadProjectMutation.mutateAsync(projectId);
      setIsOpen(false);
      toast.success(t('projectSelector.toast.loadSuccess'));
    } catch (error) {
      console.error('[ProjectSelector] Error loading project:', error);
      toast.error(t('projectSelector.toast.loadError'));
    }
  };

  const createNewProject = async () => {
    if (!newProjectName.trim()) return;

    try {
      await createProjectMutation.mutateAsync({
        name: newProjectName,
        description: newProjectDescription
      });

      // Close dialog and reset form
      setShowNewProjectDialog(false);
      setNewProjectName('');
      setNewProjectDescription('');
      toast.success(t('projectSelector.toast.createSuccess'));
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(t('projectSelector.toast.createError'));
    }
  };

  const deleteProject = async (projectId: string, projectName: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the parent button's onClick

    const confirmed = window.confirm(t('projectSelector.confirm.deleteProject', { projectName }));
    if (!confirmed) return;

    try {
      await deleteProjectMutation.mutateAsync(projectId);
      toast.success(t('projectSelector.toast.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error(t('projectSelector.toast.deleteError'));
    }
  };

  const startEditing = (projectId: string, projectName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setEditingProjectId(projectId);
    setEditingName(projectName);
    setTimeout(() => editInputRef.current?.select(), 0);
  };

  const saveProjectName = async () => {
    const projectId = editingProjectId;
    if (!projectId || !editingName.trim()) {
      setEditingProjectId(null);
      return;
    }
    // Clear editing state first to prevent double-fire (Enter + blur)
    setEditingProjectId(null);
    try {
      await apiClient.put(`/projects/${projectId}`, { name: editingName.trim() });
      queryClient.invalidateQueries(['projects']);
      queryClient.invalidateQueries(['current-project']);
      toast.success(t('projectSelector.toast.renameSuccess'));
    } catch (error) {
      console.error('Error renaming project:', error);
      toast.error(t('projectSelector.toast.renameError'));
    }
  };

  const duplicateProject = async (projectId: string, projectName: string, event: React.MouseEvent) => {
    event.stopPropagation();

    try {
      await duplicateProjectMutation.mutateAsync({ projectId });
      setIsOpen(false);
      toast.success(t('projectSelector.toast.duplicateSuccess'));
    } catch (error) {
      console.error('Error duplicating project:', error);
      toast.error(t('projectSelector.toast.duplicateError'));
    }
  };

  const getProjectStatusIcon = (project: Project | undefined) => {
    if (!project || !project.has_data) {
      return <FolderOpen className="w-4 h-4 text-gray-400" />;
    }
    
    if (project.has_data.complete) {
      return <Check className="w-4 h-4 text-green-500" />;
    } else if (project.has_data.consumption || project.has_data.bill || project.has_data.prices) {
      return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    }
    return <FolderOpen className="w-4 h-4 text-gray-400" />;
  };

  const getProjectStatusText = (project: Project | undefined) => {
    if (!project || !project.has_data) {
      return t('projectSelector.status.noData');
    }

    const dataTypes = [];
    if (project.has_data.consumption) dataTypes.push(t('projectSelector.dataType.consumption'));
    if (project.has_data.bill) dataTypes.push(t('projectSelector.dataType.bill'));
    if (project.has_data.prices) dataTypes.push(t('projectSelector.dataType.prices'));

    if (dataTypes.length === 0) return t('projectSelector.status.noData');
    if (dataTypes.length === 3) return t('projectSelector.status.complete');
    return dataTypes.join(', ');
  };

  return (
    <>
      <div className="relative">
        <button
          onClick={() => {
            setIsOpen(!isOpen);
          }}
          disabled={loading}
          className={clsx(
            "w-full flex items-center justify-between px-4 py-2.5 rounded-lg",
            "bg-white border border-swiss-mist shadow-sm",
            "hover:bg-swiss-pearl transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-opacity-50",
            loading && "opacity-50 cursor-not-allowed"
          )}
        >
          <div className="flex items-center space-x-3">
            <FolderOpen className="w-5 h-5 text-swiss-stone" />
            <div className="text-left">
              <div className="text-sm font-medium text-swiss-charcoal">
                {currentProject ? currentProject.name : t('projectSelector.button.selectProject')}
              </div>
              {currentProject && (
                <div className="text-xs text-swiss-stone">
                  {getProjectStatusText(projects.find(p => p.id === currentProject.id))}
                </div>
              )}
            </div>
          </div>
          <ChevronDown className={clsx(
            "w-4 h-4 text-swiss-stone transition-transform",
            isOpen && "transform rotate-180"
          )} />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-swiss-mist z-50 max-h-96 overflow-y-auto">
            {/* New Project Button */}
            <button
              onClick={() => {
                setShowNewProjectDialog(true);
                setIsOpen(false);
              }}
              className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-swiss-pearl transition-colors border-b border-swiss-mist"
            >
              <Plus className="w-4 h-4 text-brand-primary" />
              <span className="text-sm font-medium text-brand-primary">{t('projectSelector.button.newProject')}</span>
            </button>

            {/* Projects List */}
            {projects.length > 0 ? (
              <div className="py-2">
                {projects.map((project: Project) => (
                  <div
                    key={project.id}
                    className={clsx(
                      "group flex items-center justify-between px-4 py-2.5 hover:bg-swiss-pearl transition-colors cursor-pointer",
                      currentProject?.id === project.id && "bg-swiss-pearl"
                    )}
                    onClick={() => loadProject(project.id)}
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      {getProjectStatusIcon(project)}
                      <div className="text-left min-w-0 flex-1">
                        {editingProjectId === project.id ? (
                          <input
                            ref={editInputRef}
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onBlur={saveProjectName}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveProjectName();
                              if (e.key === 'Escape') setEditingProjectId(null);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full text-sm font-medium text-swiss-charcoal px-1.5 py-0.5 border border-brand-primary rounded focus:outline-none focus:ring-1 focus:ring-brand-primary"
                            autoFocus
                          />
                        ) : (
                          <>
                            <div className="text-sm font-medium text-swiss-charcoal truncate">
                              {project.name}
                            </div>
                            {project.description && (
                              <div className="text-xs text-swiss-stone truncate">
                                {project.description}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      {currentProject?.id === project.id && (
                        <Check className="w-4 h-4 text-brand-primary" />
                      )}
                      <button
                        onClick={(e) => startEditing(project.id, project.name, e)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-amber-50 rounded transition-opacity"
                        title={t('projectSelector.button.renameProject')}
                      >
                        <Pencil className="w-3.5 h-3.5 text-amber-600" />
                      </button>
                      <button
                        onClick={(e) => duplicateProject(project.id, project.name, e)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-blue-50 rounded transition-opacity"
                        title={t('projectSelector.button.duplicateProject')}
                      >
                        <Copy className="w-3.5 h-3.5 text-blue-600" />
                      </button>
                      <button
                        onClick={(e) => deleteProject(project.id, project.name, e)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded transition-opacity"
                        title={t('projectSelector.button.deleteProject')}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-600" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-4 py-6 text-center text-sm text-swiss-stone">
                {t('projectSelector.message.noProjects')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* New Project Dialog */}
      {showNewProjectDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-swiss-mist">
              <h3 className="text-lg font-semibold text-swiss-charcoal">
                {t('projectSelector.dialog.createNewProject')}
              </h3>
            </div>
            
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-swiss-charcoal mb-1">
                  {t('projectSelector.dialog.projectName')}
                </label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder={t('projectSelector.dialog.projectNamePlaceholder')}
                  className="w-full px-3 py-2 border border-swiss-mist rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-opacity-50"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-swiss-charcoal mb-1">
                  {t('projectSelector.dialog.description')}
                </label>
                <textarea
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  placeholder={t('projectSelector.dialog.descriptionPlaceholder')}
                  rows={3}
                  className="w-full px-3 py-2 border border-swiss-mist rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-opacity-50"
                />
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-swiss-mist flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNewProjectDialog(false);
                  setNewProjectName('');
                  setNewProjectDescription('');
                }}
                className="px-4 py-2 text-sm font-medium text-swiss-charcoal hover:bg-swiss-pearl rounded-lg transition-colors"
              >
                {t('projectSelector.button.cancel')}
              </button>
              <button
                onClick={createNewProject}
                disabled={!newProjectName.trim() || loading}
                className={clsx(
                  "px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors",
                  "bg-brand-primary hover:bg-brand-primary-dark",
                  (!newProjectName.trim() || loading) && "opacity-50 cursor-not-allowed"
                )}
              >
                {loading ? t('projectSelector.button.creating') : t('projectSelector.button.createProject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}